import { Children, isValidElement, type ReactNode } from "react";
import {
    CONTAINER_LINE_REGEX,
    ESCAPE_NUMBERS_REGEX,
    FENCED_CODE_REGEX,
    NEW_LINE_RUN_REGEX,
    NON_STANDARD_NEW_LINE_REGEX,
    OPEN_LINK_DESTINATION_REGEX,
    TERMINAL_LINE_REGEX,
    URL_LINE_END_REGEX,
} from "./markdown.constants";

/**
 * Flatten an element's children to plain text for marker-syntax matching. Hard breaks produced by `preserveNewlines`
 * arrive as `<br>` elements — map them back to newlines (and recurse into nested elements) instead of letting
 * `join("")` stringify them as "[object Object]", which corrupts markers spanning multiple lines.
 * @param children The React children of the element.
 * @returns The children flattened to a plain-text string.
 */
export const getChildrenText = (children: ReactNode): string => {
    return Children.toArray(children)
        .map((child) => {
            if (typeof child === "string" || typeof child === "number") {
                return String(child);
            }
            if (isValidElement(child)) {
                if (child.type === "br") {
                    return "\n";
                }
                return getChildrenText((child.props as { children?: ReactNode }).children);
            }
            return "";
        })
        .join("");
};

/** Any line that starts markdown block syntax — newlines before these must stay real so the block still parses. */
const isBlockSyntaxLine = (line: string) => CONTAINER_LINE_REGEX.test(line) || TERMINAL_LINE_REGEX.test(line);

/**
 * Convert the newlines in one fence-free segment into markdown hard breaks (`\<newline>`, rendered as `<br>`),
 * keeping newlines real wherever converting them would corrupt block structure or leave a visible backslash:
 * - before a block-syntax line (list item, heading, blockquote, table row, fence, rule) so the block still parses;
 * - after a line-terminated block (heading, table row, fence, rule) where the paragraph cannot continue;
 * - blank-line runs after list items and blockquotes, so following text is not pulled into the container;
 * - at the end of the segment, where a hard break cannot apply;
 * - after a URL closing a link or image destination, where the backslash would end up inside the URL.
 * A break after a bare URL anywhere else gets a leading space so the GFM autolink ends before the backslash
 * instead of absorbing it into the href.
 * Runs of N newlines between plain-text lines become N consecutive hard breaks so intentional vertical spacing is
 * preserved instead of collapsing into a single paragraph break.
 * @param segment A segment of markdown text containing no fenced code blocks.
 * @returns The segment with soft newlines converted to hard breaks.
 */
const convertNewlinesToHardBreaks = (segment: string) => {
    return segment.replace(NEW_LINE_RUN_REGEX, (run, offset: number) => {
        const rest = segment.slice(offset + run.length);
        if (rest === "") {
            return run;
        }

        const lineBefore = segment.slice(segment.lastIndexOf("\n", offset - 1) + 1, offset);
        const nextNewLineIndex = rest.indexOf("\n");
        const lineAfter = nextNewLineIndex === -1 ? rest : rest.slice(0, nextNewLineIndex);
        const newLineCount = run.split("\n").length - 1;

        if (isBlockSyntaxLine(lineAfter) || TERMINAL_LINE_REGEX.test(lineBefore)) {
            return run;
        }
        if (newLineCount > 1 && CONTAINER_LINE_REGEX.test(lineBefore)) {
            return run;
        }
        if (URL_LINE_END_REGEX.test(lineBefore)) {
            // A hard-break backslash straight after a URL is absorbed into the GFM autolink, corrupting the href
            // (GAS-1110). Inside a link or image destination — `![name](url<newline>)` — the newline must stay
            // real for the destination to close. Elsewhere a space in front of the break ends the autolink and is
            // invisible in the rendered output.
            const closesLinkDestination =
                OPEN_LINK_DESTINATION_REGEX.test(lineBefore) && lineAfter.trimStart().startsWith(")");
            return closesLinkDestination ? run : ` ${"\\\n".repeat(newLineCount)}`;
        }
        return "\\\n".repeat(newLineCount);
    });
};

/**
 * Convert literal and escaped newlines into markdown hard breaks so `\n` separators render as visible line breaks
 * without injecting `<br>` before parsing — inline HTML breaks list/block structure because markdown needs real
 * newline characters between items. Fenced code blocks are left untouched.
 * @param markdownString
 * @returns A markdown string with newlines converted to hard-break syntax.
 */
export const preserveNewlines = (markdownString: string) => {
    const normalised = markdownString.replace(NON_STANDARD_NEW_LINE_REGEX, "\n");
    return normalised
        .split(FENCED_CODE_REGEX)
        .map((segment = "", index) => (index % 2 === 1 ? segment : convertNewlinesToHardBreaks(segment)))
        .join("");
};

/**
 * Preprocess the string to escape periods in numbered sequences that are likely not intended as markdown lists.
 * For example, "1234. hello" will become "1234\\. hello", preventing markdown from interpreting it as a list.
 * @param message The markdown string to preprocess.
 * @returns A preprocessed markdown string with large numbered sequences escaped.
 */
export const preprocessString = (message: string) => {
    return message.replace(ESCAPE_NUMBERS_REGEX, (match, p1, p2) => {
        // Only replace if the number is large enough to not be a common list item
        if (p2.length > 2) {
            return `${p1}${p2}\\. `;
        }
        return match;
    });
};

/**
 * Name an attachment for the "unable to render" placeholder. The Watcher writes attachments as `![filename](url)`, so
 * the alt text is normally the filename; fall back to the last path segment of the URL when it is missing or blank.
 *
 * The path is taken via `URL` so that a query string is excluded — signed and expiring attachment URLs carry their
 * credentials there — and so that a URL with no path does not present its own domain as the filename.
 * @param source The attachment URL.
 * @param alt    The markdown alt text, if any.
 * @returns A filename to show alongside the URL.
 */
export const getAttachmentFileName = (source: string, alt?: string) => {
    if (typeof alt === "string" && alt.trim() !== "") {
        return alt.trim();
    }

    let path: string;
    try {
        path = new URL(source).pathname;
    } catch {
        // Not an absolute URL. Treat the whole thing as a path, dropping any query string or fragment by hand.
        [path = ""] = source.split(/[?#]/);
    }

    const segments = path.split("/").filter((segment) => segment !== "");
    return segments[segments.length - 1] ?? "Attachment";
};
